import { all, takeEvery, fork, put, select, call, cancelled, takeLatest, race, take } from 'redux-saga/effects';
import isEmpty from 'lodash.isempty';

import { stronaPrzedmiotuActionTypes, stronaPrzedmiotuActionsInterfaces, stronaPrzedmiotuActions } from '.';
import { dynamicTabsActions, dynamicTabsActionTypes, dynamicTabsActionsInterfaces } from '..';
import { getColumnsConfig as StudentsGetColumnsConfigSaga } from '../../staticViews/students/saga';
import { getColumnsConfig as StudentsGetColumnsConfigAction } from '../../staticViews/students/actions';
import { getColumnsConfig as MySubjectsGetColumnsConfigAction } from '../../staticViews/mySubjects/actions';
import { getColumnsConfig as MySubjectsGetColumnsConfigSaga } from '../../staticViews/mySubjects/saga';
import { copyFinalMarksFail, closeCopyFinalMarksModal, setStudentsInCopyFinalMarkModal } from '../../appConfig/actions';
import { getCopyFinalMarksData, getCopyFinalMarkStudentData, getCopyFinalMarksTabId } from '../../appConfig/selectors';
import { COPY_FINAL_MARKS_MODAL_CLOSE, PROTOCOL_GET_STATUS_SUCCESS } from '../../appConfig/types';
import { executeErrorCode } from '../../auth/actions';
import { studentsActionsInterfaces } from '../../staticViews/students';
import { getStaticViewState } from '../../staticViews/selectors';
import { studentFinderActionsInterfaces, studentFinderActionsSelectors } from '../../staticViews/studentFinder';
import { STUDENTS_COPY_FINAL_MARKS_SUCCESS } from '../stronaPrzedmiotu/types';
import {
  CREATE_FINAL_MARKS_SUCCESS,
  UPDATE_FINAL_MARKS_SUCCESS,
  CREATE_FINAL_MARK_SUCCESS,
  UPDATE_FINAL_MARK_SUCCESS,
  DELETE_FINAL_MARK_SUCCESS,
} from '../../marks/types';

import utilityActions from '../../utility/actions';
import { NotificationTypes } from '../../utility/types';
import { getTabData, getDynamicTabListFromStore, getActiveTab } from '../selectors';
import { GET_DATA_STUDENT_LIST_WITH_MARKS_CANCEL } from './types';

import marksService from '../../../services/marks';
import MySubjectsService from '../../../services/mySubjects';
import StudentsService from '../../../services/students';

import StringBuilder from '../../../helpers/StringBuilder';
import DataDestructuring from '../../../helpers/DataDestructuring';
import UrlBuilder, { getAttributes } from '../../../helpers/urlBuilder';
import { codes } from '../../../settings/codes';
import { AnyAction } from 'redux';

const { showNotification } = utilityActions;

const mandatoryFilters = ['semestrAkademicki'];
const marksColumns: string[] = [
  'ocenaKoncowa',
  'formyZajec',
  'ocenaWyliczona',
  'kartaTyp',
  'ocenaKoncowaData',
  'przedmiotTypOceny',
  'czyOcenaReczna',
  'kartaOcenId',
];
const mandatoryColumns: string[] = [
  ...marksColumns,
  'studentId',
  'studentImie',
  'studentNazwisko',
  'przedmiotKod',
  'rokAkademicki',
  'semestrAkademicki',
  'studentAlbum',
  'przedmiotId',
  'studentWydzial',
  'wpisujacyOceneKoncowaId',
];
const detailsAdditionalMandatoryColumns: string[] = [
  'oceny',
  'kartaTypKod',
  'formyZajec',
  'kartaOcenId',
  'przedmiotTypOceny',
  'studentZdjecie',
  'wpisujacyOceneKoncowaNazwa',
];
const downloadFilesColumns = [
  'studentId',
  'studentImie',
  'studentNazwisko',
  'studentAlbum',
  'rokAkademicki',
  'semestrAkademicki',
  'grupaDziekanska',
  'studentWydzial',
];
const studentListWithMarksToPrintColumns = [
  'studentNazwisko',
  'studentImie',
  'studentAlbum',
  'ocenaWyliczona',
  'ocenaKoncowaText',
  'grupaDziekanska',
  'ocenaKoncowa',
];

export function* initStronaPrzedmiotuWatcher(): Generator {
  yield takeEvery(stronaPrzedmiotuActionTypes.INIT, initStronaPrzedmiotu);
}
/**
 * inicjalizacja pobierania danych o przedmiocie, którego dotyczy zakładka
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* initStronaPrzedmiotu(action: stronaPrzedmiotuActionsInterfaces.Init): Generator {
  const { tabId } = action.payload;
  const { loading, columns } = (yield select(state => getStaticViewState(state, 'Students'))) as ViewDataSet;
  /*
    czekamy, aż pobiorą się informacje o przedmiocie, którego dotyczy zakładka oraz konfiguracja kolumn dla tabeli "Studenci".
    - kolejne requesty wymagają pewnych informacji o przedmiocie
    - tabela korzysta z tej samej konfiguracji kolumn, co tabela "Studenci".
    */
  yield all([
    call(getSubject, stronaPrzedmiotuActions.getLeftPanelData(tabId)),
    !columns.length && !loading ? call(StudentsGetColumnsConfigSaga, StudentsGetColumnsConfigAction('Students')) : null,
  ]);
  /*
    Lista filtrów oraz lista studentów dla strony przedmiotu pobierają się jednocześnie, ponieważ nie są na tym etapie od siebie zależne.
    Te wywołania mogą nastąpić dopiero w momencie kiedy są dostępne następujące zasoby:
    - informacje o przedmiocie (lewy panel)
    - konfiguracja kolumn
  */
  yield all([
    call(getFiltersConfig, stronaPrzedmiotuActions.getFiltersList(tabId)),
    call(getStudentsData, stronaPrzedmiotuActions.getData(tabId)),
  ]);

  yield put(dynamicTabsActions.initTableFinish(tabId));
  yield put(dynamicTabsActions.initFinish(tabId));
}

/**
 * pobieranie danych o przedmiocie, którego dotyczy zakładka
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getSubject(action: stronaPrzedmiotuActionsInterfaces.GetLeftPanelData): Generator {
  const { tabId } = action.payload;
  try {
    const { loading, data, columns } = (yield select(state => getStaticViewState(state, 'MySubjects'))) as ViewDataSet;
    const studentList = (yield select(state =>
      studentFinderActionsSelectors.getSubjectList(state),
    )) as studentFinderActionsInterfaces.SubjectForStudentList;
    const studentListData = studentList.data;
    /*
      Pobieranie configu kolumn dla "MySubjects".
      Konfiguracja kolumn jest niezbędna do wyświetlania danych o przedmiocie po lewej stronie zakładki pojedynczego przedmiotu
      (SubjectPage).
      - uruchamiany, jeśli nie jest ona dostępna w redux staticViews -> MySubjects
    */
    yield all([
      columns.length === 0 && !loading
        ? call(MySubjectsGetColumnsConfigSaga, MySubjectsGetColumnsConfigAction('MySubjects'))
        : null,
    ]);
    const { itemId } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
    /*
      itemId jest identyfikatorem zakładki i częścią URLa zakładek dynamicznych.
      Dla zakładki pojedycznego przedmiotu jest pełnym identyfikatorem przedmiotu, ktorego dotyczy zakładka. Składają się na niego:
        - id przedmiotu - przedmiotId
        - identyfikator semestru np. 2021-L
        - kod wydziału.
        Poniżej wyciągane są identyfikatory przedmiotu do zmiennych.
      */
    const [przedmiotId, semestrAkademicki, wydzialZlecajacyKod] = itemId.split('_');
    const [rok, semestr] = semestrAkademicki.split('-');
    /*
      Jeśli dane o przedmiocie są już dostępne (wcześniej została zainicjalizowana zakładka Moje Przedmioty
      a strona przedmiotu została uruchomiona kliknięciem w przedmiot na liście) - informacje o przedmiocie przepisywane są z listy "Moje Przedmioty".
      Jeżeli dany przedmiot nie znajduje się na liście w zakładce "Moje przedmioty" szuka informacji o nim w zakładce z przedmiotami danego studenta
      i z niego pobiera informacje, aby nie był potrzebny request.
      W przypadku inicjalizacji zakładki z linku lub odświeżeniu strony wysyłany jest request o dane przedmiotu.
      W ten sposób zapobiegamy wysyłaniu niepotrzebnego requestu o dane, które są już dostępne w aplikacji i przyspieszamy inicjalizację.
    */
    const recordFromList =
      data.length &&
      data.find(
        record =>
          record.przedmiotId.toString() === przedmiotId &&
          record.wydzialZlecajacyKod === wydzialZlecajacyKod &&
          record.semestrAkademicki === semestr &&
          record.rokAkademicki.toString() === rok,
      ) !== undefined
        ? data.find(
            record =>
              record.przedmiotId.toString() === przedmiotId &&
              record.wydzialZlecajacyKod === wydzialZlecajacyKod &&
              record.semestrAkademicki === semestr &&
              record.rokAkademicki.toString() === rok,
          )
        : studentListData.length &&
          studentListData.find(
            record =>
              record.przedmiotId.toString() === przedmiotId &&
              record.wydzialZlecajacyKod === wydzialZlecajacyKod &&
              record.semestrAkademicki === semestr &&
              record.rokAkademicki.toString() === rok,
          ) !== undefined
        ? studentListData.find(
            record =>
              record.przedmiotId.toString() === przedmiotId &&
              record.wydzialZlecajacyKod === wydzialZlecajacyKod &&
              record.semestrAkademicki === semestr &&
              record.rokAkademicki.toString() === rok,
          )
        : undefined;

    recordFromList
      ? yield put(dynamicTabsActions.getLeftPanelDataSuccess(tabId, recordFromList, recordFromList.przedmiotNazwa))
      : yield call(requestForLeftPanelData, tabId, przedmiotId, semestrAkademicki, wydzialZlecajacyKod);
  } catch (error) {
    yield put(dynamicTabsActions.getLeftPanelDataFail(tabId));
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

/** funkcja wysyłająca request o dane do lewego panelu w przedmiocie */
export function* requestForLeftPanelData(
  tabId: DynamicTabComponent['id'],
  przedmiotId: string,
  semestrAkademicki: string,
  wydzialZlecajacyKod: string,
): Generator {
  const { columns } = (yield select(state => getStaticViewState(state, 'MySubjects'))) as ViewDataSet;
  const mandatoryColumns = ['typPrzydzialu'];
  const cols = columns.map(column => column.dataIndex);
  const allRequiredColumns = DataDestructuring.prepareColumnsAsParam(cols, mandatoryColumns);
  const response: any = yield call(MySubjectsService.getMySubjectDetails, {
    id: przedmiotId,
    columns: allRequiredColumns,
    filter: UrlBuilder.generateFiltersArray({ semestrAkademicki, wydzialZlecajacyKod }),
  });
  yield put(dynamicTabsActions.getLeftPanelDataSuccess(tabId, response.przedmiot, response.przedmiot.przedmiotNazwa));
}

export function* refreshLeftPanelDataWatcher(): Generator {
  yield takeEvery(
    [
      CREATE_FINAL_MARKS_SUCCESS,
      UPDATE_FINAL_MARKS_SUCCESS,
      CREATE_FINAL_MARK_SUCCESS,
      UPDATE_FINAL_MARK_SUCCESS,
      DELETE_FINAL_MARK_SUCCESS,
      STUDENTS_COPY_FINAL_MARKS_SUCCESS,
      PROTOCOL_GET_STATUS_SUCCESS,
    ],
    refreshLeftPanelData,
  );
}

/**
 * odświeżanie szczegółów przedmiotu (lewego panelu) po zmianie inforamcji
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* refreshLeftPanelData(action: AnyAction): Generator {
  const tabId = (yield select(getActiveTab)) as DynamicTabsState['activeTab'];
  try {
    const tabList = (yield select(getDynamicTabListFromStore)) as TabList;
    const activeTabData = tabList.find(tab => tab.id === tabId);

    if (activeTabData) {
      const [przedmiotId, semestrAkademicki, wydzialZlecajacyKod] = activeTabData.itemId.split('_');

      yield call(requestForLeftPanelData, tabId, przedmiotId, semestrAkademicki, wydzialZlecajacyKod);
    }
  } catch (error) {
    yield put(dynamicTabsActions.getLeftPanelDataFail(tabId));
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getStudentsDataWatcher(): Generator {
  yield takeLatest(stronaPrzedmiotuActionTypes.GET_DATA_START, getStudentsData);
}
/**
 * pobieranie danych do tabeli "Studenci"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getStudentsData(action: stronaPrzedmiotuActionsInterfaces.GetData): Generator {
  const { tabId } = action.payload;
  const abortController = new AbortController();
  try {
    /* Pobieramy setup kolumn użytkownika dla "Students" - zakładka strony przedmiotu nie posiada oddzielnego setupu */
    const { userColumnsSetup, columns } = (yield select(state => getStaticViewState(state, 'Students'))) as ViewDataSet;
    const { table, leftPanel } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
    if (table.columns.length === 0) {
      yield put(dynamicTabsActions.getColumnsConfigSuccess(tabId, JSON.parse(JSON.stringify(columns))));
    }
    if (Object.keys(leftPanel.data).length) {
      const { filters, sorting } = table;
      const { wydzialZlecajacyKod, przedmiotId, rokAkademicki, semestrAkademicki, typPrzydzialu } = leftPanel.data;
      const semester = StringBuilder.buildSemester(rokAkademicki, semestrAkademicki);
      const columns = DataDestructuring.prepareColumnsAsParam(userColumnsSetup.main, mandatoryColumns);
      const requestAtributes: getAttributes = {
        filter: [
          ...filters.allFilters.map(filters => ({
            name: filters.name,
            values: filters.selected.map(filter => (filter === '' ? null : filter)),
          })),
          ...UrlBuilder.generateFiltersArray({ semestrAkademicki: semester, wydzialZlecajacyKod, typPrzydzialu }),
        ],
        page: 1,
        columns,
        sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
        orderBy: sorting.columnName,
        przedmiotId,
        semester,
      };

      let response: any = yield call(StudentsService.getStudentsForSubject, requestAtributes, abortController.signal);
      /* W momencie kiedy zapytanie zwróci pustą tablicę/obiekt bądź nie będzie zwierało poprawnej daty wykona się zapytanie z no cache */
      if (!response.studentPage.data || isEmpty(response.studentPage.data)) {
        response = yield call(StudentsService.getStudentsForSubject, requestAtributes, abortController.signal, true);
      }
      /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
      const payload: GetDataSuccessPayload = {
        ...response.studentPage,
        cacheDuration: response.cacheTime,
        controlSum: response.controlSum === 'filters' ? '' : response.controlSum,
        data: response.studentPage.data.map((obj: any, index: number) => ({
          ...obj,
          key: obj.studentId,
          lp: index + 1,
        })),
      };
      yield put(dynamicTabsActions.getDataSuccess(tabId, payload));
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(dynamicTabsActions.getDataFail(tabId));
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}
export function* getStudentsListWithMarksDataWatcher(): Generator {
  yield takeLatest(stronaPrzedmiotuActionTypes.GET_DATA_STUDENT_LIST_WITH_MARKS_START, getStudentsListWithMarksData);
}
/**
 * pobieranie danych drukowalnej listy ocen
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getStudentsListWithMarksData(action: stronaPrzedmiotuActionsInterfaces.GetData): Generator {
  const { tabId } = action.payload;
  const abortController = new AbortController();
  try {
    const { table, leftPanel, studentWithMarksData } = (yield select(state =>
      getTabData(state, tabId),
    )) as DynamicTabWithTable<MySubjectsData>;
    if (Object.keys(leftPanel.data).length) {
      const { filters } = table;
      const { wydzialZlecajacyKod, przedmiotId, rokAkademicki, semestrAkademicki, typPrzydzialu } = leftPanel.data;
      const semester = StringBuilder.buildSemester(rokAkademicki, semestrAkademicki);
      const requestAtributes: getAttributes = {
        filter: [
          ...filters.allFilters.map(filters => ({
            name: filters.name,
            values: filters.selected.map(filter => (filter === '' ? null : filter)),
          })),
          ...UrlBuilder.generateFiltersArray({ semestrAkademicki: semester, wydzialZlecajacyKod, typPrzydzialu }),
        ],
        page: 1,
        columns: studentListWithMarksToPrintColumns,
        sort: studentWithMarksData?.sorting?.order ? (studentWithMarksData?.sorting.order === 'ascend' ? 'asc' : 'desc') : null,
        orderBy: studentWithMarksData?.sorting?.columnName,
        przedmiotId,
        semester,
      };
      const { cancel, response }: any = yield race({
        response: call(StudentsService.getStudentsWithMarksToPrint, requestAtributes, abortController.signal),
        cancel: take(GET_DATA_STUDENT_LIST_WITH_MARKS_CANCEL),
      });
      if (cancel) {
        abortController.abort();
      } else {
        /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
        const payload: GetDataSuccessPayload = {
          ...response.studentPage,
          data: response.studentPage.data.map((obj: any, index: number) => ({
            ...obj,
            key: obj.studentId,
            lp: index + 1,
          })),
        };
        yield put(stronaPrzedmiotuActions.getDataStudentListWithMarksSuccess(tabId, payload));
      }
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(stronaPrzedmiotuActions.getDataStudentListWithMarksFail(tabId));
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}
export function* refreshFilterValuesWatcher(): Generator {
  yield takeLatest(stronaPrzedmiotuActionTypes.FILTERS_REFRESH_VALUES_START, refreshFilterValues);
}
/**
 * aktualizacja wartości filtrów
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* refreshFilterValues(action: stronaPrzedmiotuActionsInterfaces.RefreshFilterValues): Generator {
  const { tabId } = action.payload;
  const abortController = new AbortController();
  try {
    const { table, leftPanel } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
    // const { przedmiotId, rokAkademicki, semestrAkademicki, wydzialZlecajacyKod } = leftPanel.data;
    /*
      budowa parametrów do prośby o aktualizację listy filtrów
      - funkcja reduce buduje atrybuty z filtrów, które użytkownik sam wybrał,
      - przedmiotId, semestrAkademicki oraz wydzialZlecajacyKod są identyfikatorami przedmiotu, którego dotyczy zakładka.
        Są niezbędne, aby zwrócić indywidualną listę wartości filtrów dla zakładki.
      */
    const { przedmiotId, rokAkademicki, semestrAkademicki, wydzialZlecajacyKod } = leftPanel.data;
    const requestAttributes: FilterValuePayload = {
      filter: {
        ...table.filters.allFilters
          .filter(filter => filter.active)
          .reduce((acc: object, filter) => {
            return { ...acc, [filter.name]: DataDestructuring.returnMultiFilterData(filter) };
          }, {}),
        // dołączanie identyfikatorów przedmiotu jako obowiązkowe filtry dla tego widoku
        semestrAkademicki: StringBuilder.buildSemester(rokAkademicki, semestrAkademicki),
        przedmiotId,
        wydzialZlecajacyKod,
      },
    };
    const response: any = yield call(StudentsService.refreshFilterValues, requestAttributes, abortController.signal);
    yield put(dynamicTabsActions.refreshFilterValueSuccess(tabId, response.response));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    // verify: czy potrzebny jest error w payload - on był do noAccess
    yield put(dynamicTabsActions.refreshFilterValueFail(tabId));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

/**
 * pobieranie konfiguracji filtrów dla tabeli "Studenci".
 * Zakładka strona-przedmiotu korzysta z tej samej konfiguracji filtrów, co zakładka "Studenci",
 * ale dodatkowo usuwany jest filtr semestru akademickiego.
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getFiltersConfig(action: stronaPrzedmiotuActionsInterfaces.GetFiltersList): Generator {
  const { tabId } = action.payload;
  /*
    Sprawdzamy, czy istnieje już pobrany config w jednej z zakładek strona-przedmiotu.
    Jeśli tak, to od razu wtłaczamy go do stanu zakładki i pobieramy tylko wartości filtrów.
  */
  const tabs = (yield select(getDynamicTabListFromStore)) as TabList;
  const tabWithFilterConfig = tabs.find(
    tab => tab.tabType === 'strona-przedmiotu' && tab.table.filters.allFilters.length,
  ) as DynamicTabWithTable<StudentsData>;
  if (tabWithFilterConfig) {
    yield put(dynamicTabsActions.getFiltersConfigSuccess(tabId, tabWithFilterConfig.table.filters.allFilters));
    yield call(refreshFilterValues, stronaPrzedmiotuActions.refreshFilterValues(tabId));
  } else {
    try {
      const response: any = yield call(StudentsService.getStudentsFilterList);
      /*
        Po otrzymaniu odpowiedzi usuwane są z listy filtry, które nie są potrzebne w zakładce strona-przedmiotu:
        - semestrAkademicki - nie filtrujemy po semestrze, bo lista studentów dotyczy przedmiotu w konkretnym semestrze.
      */
      const fieldFilter = response.fieldFilter.filter((filter: any) => !mandatoryFilters.includes(filter.name));
      yield put(dynamicTabsActions.getFiltersConfigSuccess(tabId, fieldFilter));
      yield call(refreshFilterValues, stronaPrzedmiotuActions.refreshFilterValues(tabId));
    } catch (error) {
      if (error && error.errorCode) {
        yield put(executeErrorCode(error.errorCode, action, error));
      }
      yield put(dynamicTabsActions.getFiltersConfigFail(tabId));
    }
  }
}

export function* refreshStudentsFilterValuesWatcher(): Generator {
  yield takeEvery(
    [
      dynamicTabsActionTypes.FILTERS_SELECT_VALUES,
      dynamicTabsActionTypes.FILTERS_CLOSE_CATEGORY,
      dynamicTabsActionTypes.FILTERS_OPEN_CATEGORY,
    ],
    refreshStudentsFilterValues,
  );
}
/**
 * inicjalizacja odświeżenia listy wartości filtrów
 *
 * @export
 * @return {*}  {Generator}
 */
export function* refreshStudentsFilterValues(
  action:
    | dynamicTabsActionsInterfaces.OpenFilterCategory
    | dynamicTabsActionsInterfaces.CloseFilterCategory
    | dynamicTabsActionsInterfaces.SelectFilterValues,
): Generator {
  const { tabId } = action.payload;
  const tabData = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
  if (tabData && !tabData.initTable) {
    yield put(stronaPrzedmiotuActions.refreshFilterValues(tabId));
  }
}

export function* refreshTabsDataAfterChangeColumnsSetupWatcher(): Generator {
  yield takeEvery(
    [
      // STUDENTS_COLUMNS_SAVE_USER_SETUP_SUCCESS,
    ],
    refreshTabsDataAfterChangeColumnsSetup,
  );
}
/**
 * inicjalizacja odświeżenia danych po zmianie konfiguracji kolumn. Wykonywana dla każdej otwartej zakładki o typie 'strona-przedmiotu'
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* refreshTabsDataAfterChangeColumnsSetup(
  action: studentsActionsInterfaces.SaveColumnsUserSetupSuccess,
): Generator {
  const tabs = (yield select(getDynamicTabListFromStore)) as TabList;
  for (const iterator of tabs) {
    if (!iterator.initTable && iterator.tabType === 'strona-przedmiotu' && iterator.wasInitialized) {
      yield put(stronaPrzedmiotuActions.getData(iterator.id));
      if (iterator.table.expanded) {
        yield put(stronaPrzedmiotuActions.getRecordDetails(iterator.id, iterator.table.expanded, true));
      }
    }
  }
}

export function* refreshStudentsDataWatcher(): Generator {
  yield takeEvery(
    [
      stronaPrzedmiotuActionTypes.FILTERS_REFRESH_VALUES_START,
      stronaPrzedmiotuActionTypes.STUDENTS_COPY_FINAL_MARKS_SUCCESS,
      stronaPrzedmiotuActionTypes.REFRESH_DATA_START,
    ],
    refreshStudentsData,
  );
}
/**
 * inicjalizacja odświeżenia listy
 *
 * @export
 * @return {*}  {Generator}
 */
export function* refreshStudentsData(action: stronaPrzedmiotuActionsInterfaces.RefreshFilterValues): Generator {
  const { tabId } = action.payload;
  const { table, leftPanel, initTable } = (yield select(state =>
    getTabData(state, tabId),
  )) as DynamicTabWithTable<MySubjectsData>;
  if (!table.initTable && Object.keys(leftPanel.data).length && !initTable) {
    yield put(stronaPrzedmiotuActions.getData(tabId));
    if (table.expanded) {
      yield put(dynamicTabsActions.setDetailsLoading(tabId));
      yield put(stronaPrzedmiotuActions.getRecordDetails(tabId, table.expanded, true));
    }
  }
}

export function* getStudentsDetailsWatcher(): Generator {
  yield takeEvery([stronaPrzedmiotuActionTypes.RECORD_GET_DETAILS_START], getStudentsDetails);
}
/**
 * pobieranie szczegółów wybranego rekordu
 *
 * @export
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* getStudentsDetails(action: stronaPrzedmiotuActionsInterfaces.GetRecordDetails): Generator {
  const { tabId, refresh, recordId } = action.payload;
  try {
    const { table, leftPanel } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
    const { userColumnsSetup } = (yield select(state => getStaticViewState(state, 'Students'))) as ViewDataSet;
    const requiredCols = [...userColumnsSetup.details, ...detailsAdditionalMandatoryColumns].filter(
      (item, idx, self) => self.indexOf(item) === idx,
    );
    const columns = DataDestructuring.prepareColumnsAsParam(requiredCols, mandatoryColumns);
    const selectedRow: any = table.data.find((row: any) => row.studentId === recordId);
    /*
      Wysyłamy request jeśli rekord nie posiada jeszcze danych szczegółowych lub wymuszamy ich odświeżenie (np. po wystawieniu ocen).
      W stanie aplikacji szczegóły rekordu są przechowywane w obiekcie rekordu na liście danych pod kluczem "details".
    */
    if (refresh || !selectedRow.details || Object.entries(selectedRow.details).length === 0) {
      const { przedmiotId, rokAkademicki, typPrzydzialu } = leftPanel.data;
      const { kartaOcenId } = selectedRow;
      const semestrAkademicki = StringBuilder.buildSemester(rokAkademicki, leftPanel.data.semestrAkademicki);
      const response: any = yield call(StudentsService.getStudentDetails, {
        id: recordId,
        columns,
        filter: UrlBuilder.generateFiltersArray({ semestrAkademicki, przedmiotId, typPrzydzialu, kartaOcenId }),
      });
      /* na potrzeby łatwiejszego zarządzania danymi w komponentach rozdzielane są dane o studencie i jego ocenach */
      const commonData = Object.keys(response.student).reduce((r: any, col: string) => {
        if (!marksColumns.includes(col)) r[col] = response.student[col];
        return r;
      }, {});
      const markData = Object.keys(response.student).reduce((r: any, col: string) => {
        if ([...marksColumns, ...detailsAdditionalMandatoryColumns].includes(col)) r[col] = response.student[col];
        return r;
      }, {});
      yield put(dynamicTabsActions.getRecordDetailsSuccess(tabId, commonData, markData, recordId));
    } else {
      /*
        Jeżeli dane nie będą pobierane ponownie, bo są już dostępne lub nie ma potrzeby odświeżania ich
        - zmień stan loading (tutaj na false).
      */
      yield put(dynamicTabsActions.setDetailsLoading(tabId));
    }
    // }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(dynamicTabsActions.getRecordDetailsFail(tabId));
  }
}

export function* refreshSelectedRecordsWatcher(): Generator {
  yield takeEvery(stronaPrzedmiotuActionTypes.REFRESH_SELECTED_DATA_START, refreshSelectedRecords);
}

/**
 * odświeżenie pojedynczego rekordu na liście
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* refreshSelectedRecords(action: stronaPrzedmiotuActionsInterfaces.RefreshSelectedData): Generator {
  const { tabId, recordsToRefresh } = action.payload;
  const abortController = new AbortController();
  try {
    const { userColumnsSetup } = (yield select(state => getStaticViewState(state, 'Students'))) as ViewDataSet;
    const { table, leftPanel } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
    const { sorting, expanded } = table;
    const { rokAkademicki, semestrAkademicki, przedmiotId, wydzialZlecajacyKod, typPrzydzialu } = leftPanel.data;
    const requestAtributes: getAttributes = {
      filter: [
        // dołączanie obowiązkowych filtrów
        ...UrlBuilder.generateFiltersArray({ semestrAkademicki, przedmiotId, wydzialZlecajacyKod, typPrzydzialu }),
        /* chcemy uzyskać dane tylko dla wybranych rekordów, więc dołączamy id wybranych studentów jako filtry do zapytania */
        { name: 'studentId', values: recordsToRefresh },
      ],
      limit: recordsToRefresh.length,
      columns: DataDestructuring.prepareColumnsAsParam(userColumnsSetup.main, mandatoryColumns),
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
      przedmiotId,
      semester: StringBuilder.buildSemester(rokAkademicki, semestrAkademicki),
    };
    let response: any = yield call(StudentsService.getStudentsForSubject, requestAtributes, abortController.signal);
    /* W momencie kiedy zapytanie zwróci pustą tablicę/obiekt bądź nie będzie zwierało poprawnej daty wykona się zapytanie z no cache */
    if (!response.studentPage.data || isEmpty(response.studentPage.data)) {
      response = yield call(StudentsService.getStudentsForSubject, requestAtributes, abortController.signal, true);
    }
    yield put(dynamicTabsActions.refreshSelectedDataSuccess(tabId, response.studentPage.data, recordsToRefresh));
    if (expanded) {
      yield call(getStudentsDetails, stronaPrzedmiotuActions.getRecordDetails(tabId, expanded, true));
    }
    // }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* copyFinalMarksBasedOnCalculatedWatcher(): Generator {
  yield takeEvery(stronaPrzedmiotuActionTypes.STUDENTS_COPY_FINAL_MARKS_START, copyFinalMarksBasedOnCalculated);
}
/**
 * wysłanie żądania o przepisanie ocen końcowych na podstawie wyliczonych
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* copyFinalMarksBasedOnCalculated(action: stronaPrzedmiotuActionsInterfaces.CopyFinalMarksStart): Generator {
  const abortController = new AbortController();
  try {
    const data = (yield select(getCopyFinalMarksData)) as Modal.CopyFinalMarks['data'];
    const tabId = (yield select(getCopyFinalMarksTabId)) as Modal.CopyFinalMarks['tabId'];
    const studentsData = (yield select(getCopyFinalMarkStudentData)) as Modal.CopyFinalMarks['studentData'];
    const kartaOcenId = studentsData.map(student => student.kartaOcenId);
    if (data) {
      const { cancel, response }: any = yield race({
        response: call(
          marksService.copyFinalMarksBasedOnCalculatedMarks,
          {
            ...data,
            kartaOcenId,
          },
          abortController.signal,
        ),
        cancel: take(COPY_FINAL_MARKS_MODAL_CLOSE),
      });
      if (cancel) {
        abortController.abort();
      } else {
        if (tabId) {
          yield put(stronaPrzedmiotuActions.copyFinalMarksSuccess(tabId));
        }

        if (response.actionCode && response.actionCode === codes.COPY_FINAL_MARKS_ERROR_FOR_LISTED_STUDENTS) {
          const studentsData = response.date.map((item: any) => item.ztnStudentDto);
          yield put(setStudentsInCopyFinalMarkModal(studentsData));
          yield put(copyFinalMarksFail(response.hash));
        } else {
          yield put(closeCopyFinalMarksModal());
        }
      }
    }
  } catch (error) {
    yield put(closeCopyFinalMarksModal());
    yield put(copyFinalMarksFail());
    if (error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getStudentsListExcelFileWatcher(): Generator {
  yield takeEvery(stronaPrzedmiotuActionTypes.DATA_GET_CSV_START, getStudentsListExcelFile);
}
/**
 * pobieranie pliku csv z listą studentów
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getStudentsListExcelFile(action: stronaPrzedmiotuActionsInterfaces.GetDataCsv): Generator {
  const { tabId } = action.payload;
  try {
    const { leftPanel, table } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
    const { filters } = table;
    if (Object.keys(leftPanel.data).length) {
      const { totalRecords, data } = table;
      if (data.length) {
        const { przedmiotId, rokAkademicki, semestrAkademicki, wydzialZlecajacyKod, typPrzydzialu, przedmiotKod } =
          leftPanel.data;
        const semester = StringBuilder.buildSemester(rokAkademicki, semestrAkademicki);
        const requestAtributes: getAttributes = {
          filter: [
            // filtry z zakładki danego przedmiotu
            ...filters.allFilters.map(filters => ({
              name: filters.name,
              values: filters.selected.map(filter => (filter === '' ? null : filter)),
            })),
            // dołączanie obowiązkowych filtrów
            ...UrlBuilder.generateFiltersArray({
              semestrAkademicki: semester,
              przedmiotId,
              wydzialZlecajacyKod,
              typPrzydzialu,
              przedmiotKod,
            }),
          ],
          page: 1,
          columns: downloadFilesColumns,
          limit: totalRecords.toString(),
          sort: table.sorting.order ? (table.sorting.order === 'ascend' ? 'asc' : 'desc') : 'asc',
          orderBy: table.sorting.columnName ? table.sorting.columnName : 'studentNazwisko',
          przedmiotId,
          semester,
          search: null,
          noCache: true.toString(),
        };
        const response = yield call(StudentsService.getStudentsListExcelFile, requestAtributes);
        const downloadUrl = URL.createObjectURL(response);
        const link = document.createElement('a');
        link.setAttribute('href', downloadUrl);
        link.setAttribute('download', `studenci-${przedmiotKod}-${rokAkademicki}-${semestrAkademicki}.csv`);
        document.body.appendChild(link);
        link.click();
        if (link && link.parentNode) link.parentNode.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      } else {
        yield put(
          showNotification({
            notifType: 'warning' as NotificationTypes,
            titleTranslationKey: 'GET_LIST_ERROR_NO_DATA',
            bodyTranslationKey: '',
          }),
        );
      }
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}
export function* syncDataWithCacheWatcher(): Generator {
  yield takeEvery(stronaPrzedmiotuActionTypes.SYNC_DATA_WITH_CACHE_START, syncDataWithCache);
}
/**
 * synchronizacja danych z cache
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* syncDataWithCache(action: stronaPrzedmiotuActionsInterfaces.SyncDataWithCache): Generator {
  const { tabId } = action.payload;
  const abortController = new AbortController();
  try {
    const { userColumnsSetup } = (yield select(state => getStaticViewState(state, 'Students'))) as ViewDataSet;
    const { leftPanel, table } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithTable<MySubjectsData>;
    if (Object.keys(leftPanel.data).length) {
      const { filters, sorting } = table;
      const { wydzialZlecajacyKod, przedmiotId, rokAkademicki, semestrAkademicki, typPrzydzialu } = leftPanel.data;
      const semester = StringBuilder.buildSemester(rokAkademicki, semestrAkademicki);
      const columns = DataDestructuring.prepareColumnsAsParam(userColumnsSetup.main, mandatoryColumns);
      const requestAtributes = {
        filter: [
          ...filters.allFilters.map(filters => ({
            name: filters.name,
            values: filters.selected.map(filter => (filter === '' ? null : filter)),
          })),
          ...UrlBuilder.generateFiltersArray({ semestrAkademicki: semester, wydzialZlecajacyKod, typPrzydzialu }),
        ],
        page: 1,
        columns,
        sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
        orderBy: sorting.columnName,
        przedmiotId,
        semester,
        noCache: true.toString(),
      };
      const { controlSum } = table.cache;
      const response: any = yield call(StudentsService.getStudentsForSubject, requestAtributes, abortController.signal);
      yield put(
        dynamicTabsActions.syncDataWithCacheSuccess(tabId, {
          controlSum: response.controlSum === 'filters' ? '' : response.controlSum,
          isUpToDate: controlSum === response.controlSum || response.controlSum === 'filters',
          duration: response.cacheTime,
        }),
      );
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(dynamicTabsActions.syncDataWithCacheFail(tabId));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}
export default function* rootSaga(): Generator {
  yield all([
    fork(initStronaPrzedmiotuWatcher),
    fork(getStudentsDataWatcher),
    fork(refreshFilterValuesWatcher),
    fork(refreshStudentsFilterValuesWatcher),
    fork(refreshStudentsDataWatcher),
    fork(getStudentsDetailsWatcher),
    fork(refreshSelectedRecordsWatcher),
    fork(refreshTabsDataAfterChangeColumnsSetupWatcher),
    fork(copyFinalMarksBasedOnCalculatedWatcher),
    fork(getStudentsListExcelFileWatcher),
    fork(syncDataWithCacheWatcher),
    fork(getStudentsListWithMarksDataWatcher),
    fork(refreshLeftPanelDataWatcher),
  ]);
}
